/**
 * Merge Tool Handler Module (ISS-04-139)
 * Holds state and orchestrates form.ts + api.ts + render.ts. Bound to DOM
 * events by merge-tool-listener.ts.
 */

import { notifier } from "../../core/notifier/notifier";
import { fetchMergeCandidates, submitMerge, type MergeEntity } from "./merge-tool-api";
import { MERGE_FIELD_CONFIG, renderCandidateButtons, renderColumns, renderLoading } from "./merge-tool-render";
import { MergeToolForm } from "./merge-tool-form";

type UnknownRecord = Record<string, unknown>;

export class MergeToolController {
    private form: MergeToolForm;

    private entity: MergeEntity = "brands";

    private sourceId = 0;

    private source: UnknownRecord | null = null;

    private candidates: UnknownRecord[] = [];

    private targetId: number | null = null;

    /** field key -> 'source' | 'target' (default when unset: 'target') */
    private selections: Record<string, "source" | "target"> = {};

    public constructor(modal: HTMLElement) {
        this.form = new MergeToolForm(modal);
    }

    public async open(entity: MergeEntity, sourceId: number): Promise<void> {
        this.entity = entity;
        this.sourceId = sourceId;
        this.targetId = null;
        this.selections = {};

        this.form.getRoot().style.display = "block";
        this.setColumnsHtml(renderLoading());
        this.setCandidatesHtml("");
        this.setSubmitEnabled(false);

        const data = await fetchMergeCandidates(entity, sourceId).catch((error: unknown) => {
            console.error(error);
            return null;
        });

        if (!data || !data.source) {
            notifier.error("Failed to load merge candidates");
            this.close();
            return;
        }

        this.source = data.source;
        this.candidates = data.candidates ?? [];

        this.renderCandidates();

        if (this.candidates.length === 1) {
            const config = MERGE_FIELD_CONFIG[this.entity];
            const onlyId = Number(this.candidates[0][config.idKey] || 0);
            if (onlyId > 0) {
                this.selectTarget(onlyId);
                return;
            }
        }

        this.renderColumnsView();
    }

    public close(): void {
        this.form.getRoot().style.display = "none";
        this.source = null;
        this.candidates = [];
        this.targetId = null;
        this.selections = {};
    }

    public selectTarget(id: number): void {
        this.targetId = id;
        this.selections = {};
        this.renderCandidates();
        this.renderColumnsView();
        this.setSubmitEnabled(true);
    }

    public selectFieldValue(field: string, side: "source" | "target"): void {
        this.selections[field] = side;
        this.renderColumnsView();
    }

    // Bound to the target card's id input (blur / Enter) — the input is the
    // only way to set a target that wasn't auto-detected, so it doubles as
    // both the initial picker (before any candidate exists) and a way to
    // override an already-selected candidate.
    public async submitManualTarget(): Promise<void> {
        const manualId = this.form.getManualTargetId();
        if (manualId === this.targetId) return;

        if (!manualId) {
            if (this.targetId !== null) {
                this.targetId = null;
                this.selections = {};
                this.renderCandidates();
                this.renderColumnsView();
                this.setSubmitEnabled(false);
            }
            return;
        }

        if (manualId === this.sourceId) {
            notifier.error("Target cannot be the same as the source");
            this.renderColumnsView();
            return;
        }

        this.setColumnsHtml(renderLoading());

        const data = await fetchMergeCandidates(this.entity, this.sourceId, manualId).catch((error: unknown) => {
            console.error(error);
            return null;
        });

        if (!data || !data.source) {
            notifier.error("Failed to load merge candidates");
            this.renderColumnsView();
            return;
        }

        this.source = data.source;
        this.candidates = data.candidates ?? [];

        const config = MERGE_FIELD_CONFIG[this.entity];
        const found = this.candidates.some((candidate) => Number(candidate[config.idKey] || 0) === manualId);
        if (!found) {
            notifier.error("That ID was not found");
            this.renderCandidates();
            this.renderColumnsView();
            return;
        }

        this.selectTarget(manualId);
    }

    public async submit(): Promise<void> {
        if (!this.targetId || this.targetId <= 0) return;

        const confirmed = await notifier.confirm("Merge this entity into the selected target? This cannot be undone.");
        if (!confirmed) return;

        const fields: UnknownRecord = {};
        for (const [key, side] of Object.entries(this.selections)) {
            if (side === "source") fields[key] = "source";
        }

        const result = await submitMerge(this.entity, this.sourceId, this.targetId, fields).catch((error: unknown) => {
            console.error(error);
            return { ok: false, message: undefined };
        });

        if (!result.ok) {
            notifier.error(result.message || "Merge failed");
            return;
        }

        notifier.success("Merged");
        const base = this.entity === "brands" ? "/brands/" : "/products/";
        window.location.href = base + this.targetId;
    }

    // Once a target is picked (by button or by typing an id directly into the
    // target card), the candidate list would just repeat what the card above
    // already shows — only worth showing while nothing is picked yet.
    private renderCandidates(): void {
        if (this.targetId !== null) {
            this.setCandidatesHtml("");
            return;
        }

        this.setCandidatesHtml(renderCandidateButtons(this.entity, this.candidates, this.targetId));
    }

    private renderColumnsView(): void {
        if (!this.source) {
            this.setColumnsHtml("");
            return;
        }

        const config = MERGE_FIELD_CONFIG[this.entity];
        const target = this.targetId
            ? (this.candidates.find((candidate) => Number(candidate[config.idKey] || 0) === this.targetId) ?? null)
            : null;

        this.setColumnsHtml(renderColumns(this.entity, this.source, target, this.selections, this.candidates.length === 0));
    }

    private setColumnsHtml(html: string): void {
        const el = this.form.getColumnsContainer();
        if (el) el.innerHTML = html;
    }

    private setCandidatesHtml(html: string): void {
        const el = this.form.getCandidatesContainer();
        if (el) el.innerHTML = html;
    }

    private setSubmitEnabled(enabled: boolean): void {
        const btn = this.form.getSubmitButton();
        if (btn) btn.disabled = !enabled;
    }
}
